import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../../types/common.types';
import { Member, MemberBadge } from '../../../../../types/entity.types';

import { useMemberBadgeApi } from '../../../../../api/member/badges/useMemberBadgeApi';

import Button from '../../../../common/button/Button';
import Image from '../../../../common/image/Image';
import NumberPicker from '../../../../common/input/number-picker/NumberPicker';
import Modal from '../../../../common/modal/Modal';

import './member-badges-modal.scss';

type Props = {
  visible: boolean;
  member?: Member;
  close: () => void;
  setMemberBadges: (memberBadges: MemberBadge[]) => void;
};

const MemberBadgesModal = ({ visible, member, close, setMemberBadges }: Props) => {
  const { memberBadges } = useSelector(({ member }: RootState) => member);

  const { viewMemberBadges, modifyMemberBadges } = useMemberBadgeApi();

  const [badges, setBadges] = useState<MemberBadge[]>([]);

  useEffect(() => {
    if (!member) return;

    viewMemberBadges({ memberExternalReference: member.externalReference! });
  }, [member, viewMemberBadges]);

  useEffect(() => {
    if (!memberBadges) {
      return;
    }

    setBadges([...memberBadges]);
  }, [memberBadges]);

  const updateBadgesSuccess = () => {
    const filteredBadges = badges.filter((badge) => badge.badgeCount !== 0);
    setMemberBadges(filteredBadges);
    close();
  };

  const handleSaveClick = () => {
    modifyMemberBadges(
      { memberExternalReference: member!.externalReference!, memberBadges: badges },
      updateBadgesSuccess
    );
  };

  const handleValueChange = (externalRef: string, newValue: number) => {
    setBadges((prev) =>
      prev.map((badge) => (badge.badgeExternalReference === externalRef ? { ...badge, badgeCount: newValue } : badge))
    );
  };

  return (
    <Modal show={visible}>
      <section className="member-badges-modal" data-testid="member-badges-modal">
        <div className="member-badges-modal__header">Badges</div>
        <div className="member-badges-modal__body">
          <p className="member-badges-modal__member">{member?.name}</p>
          {badges.map((badge) => (
            <div className="member-badges-modal__badge" key={badge.badgeExternalReference}>
              <div className="member-badges-modal__badge__icon-container">
                <Image className="member-badges-modal__badge__icon" src={badge.iconExternalReference!} />
              </div>
              <p className="member-badges-modal__badge__name">{badge.badgeName}:</p>
              <NumberPicker
                value={badge?.badgeCount ?? 0}
                onChange={(value) => handleValueChange(badge.badgeExternalReference, value as number)}
              />
            </div>
          ))}
          {badges.length === 0 && <p className="member-badges-modal__no-badges">No badges available</p>}
        </div>
        <div className="member-badges-modal__button-container">
          <Button text="Cancel" onClick={close} />
          <Button text="Save" onClick={handleSaveClick} />
        </div>
      </section>
    </Modal>
  );
};

export default MemberBadgesModal;
